import { ref } from 'vue';

// Generic expand/collapse tracking by numeric id - each call site owns its own
// independent Set, so a page can track several unrelated expandable things (e.g. the Objectives
// page's expandable Barn rows and, separately, which Farms have their trend chart open) without
// them stepping on each other. Expanded state persists only for the page's lifetime (not
// localStorage) - there is no requirement to remember it across visits, unlike the reference-window
// choice (see views/dashboard/overview/objectives/referenceWindow.ts).
export function useExpandedIds() {
    const expandedIds = ref<Set<number>>(new Set());

    function isExpanded(id: number): boolean {
        return expandedIds.value.has(id);
    }

    function toggle(id: number): void {
        const next = new Set(expandedIds.value);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        expandedIds.value = next;
    }

    // Collapses everything at once - e.g. the Objectives page resetting on an animal-type
    // switch, where the old type's expanded ids are stale, not wrong per se, but no longer "the
    // current view" the user just asked for.
    function reset(): void {
        expandedIds.value = new Set();
    }

    return { expandedIds, isExpanded, toggle, reset };
}
